import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import {
  Users,
  CalendarDays,
  IdCard,
  Search,
  X,
  UserRound,
  Eye,
} from "lucide-react";
import { db } from "../../firebase";

const BLUE = "#204080";

const formatDate = (value) => {
  if (!value) return "—";
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getSearchableValue = (value) => String(value ?? "").toLowerCase();

export default function PatientRegistrationDashboard() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "patients_regisgistration_form"),
      (snapshot) => {
        const records = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        records.sort((a, b) => {
          const dateA = a.created_at?.toDate?.()?.getTime?.() || 0;
          const dateB = b.created_at?.toDate?.()?.getTime?.() || 0;
          return dateB - dateA;
        });

        setPatients(records);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Registration records error:", err);
        setError("Unable to load registrations. Please check Firebase permissions.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredPatients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return patients;

    return patients.filter((patient) =>
      [
        patient.name,
        patient.vpjc_id,
        patient.mobile,
        patient.residentialMobile,
        patient.permanentMobile,
        patient.email,
      ].some((value) => getSearchableValue(value).includes(term))
    );
  }, [patients, search]);

  const todayCount = useMemo(() => {
    const today = new Date();
    return patients.filter((patient) => {
      const value = patient.created_at?.toDate?.();
      return (
        value &&
        value.getDate() === today.getDate() &&
        value.getMonth() === today.getMonth() &&
        value.getFullYear() === today.getFullYear()
      );
    }).length;
  }, [patients]);

  const stats = [
    {
      label: "Total Registrations",
      value: patients.length,
      icon: Users,
      description: "All registered patients",
    },
    {
      label: "Today's Registrations",
      value: todayCount,
      icon: CalendarDays,
      description: "Patients registered today",
    },
    {
      label: "VPJC IDs Available",
      value: patients.filter((patient) => patient.vpjc_id?.trim()).length,
      icon: IdCard,
      description: "Patients with an assigned ID",
    },
  ];

  const detailFields = [
    // 01 · Patient Details
    ["Previous Visit", "previousVisit"],
    ["Patient VPJC ID", "vpjc_id"],
    ["Patient Name", "name"],
    ["Age", "age"],
    ["Gender", "gender"],
    ["Father's / Husband's Name", "guardianName"],
    ["Marital Status", "maritalStatus"],
    ["Nationality", "nationality"],
    ["Occupation", "occupation"],
    ["Blood Group", "bloodGroup"],
    ["Patient Type", "patientType"],
    ["Patient Category", "patientCategory"],
    ["Payment Mode", "paymentMode"],

    // 02 · Reference Information
    ["Is MLC?", "isMLC"],
    ["Emergency Contact Person", "emergencyContactPerson"],
    ["Emergency Contact Number", "emergencyContactNumber"],
    ["Referring Doctor / Hospital", "referringDoctorHospital"],

    // 03 · Address Information
    ["Residential Address", "residentialAddress"],
    ["Residential Village / City", "residentialCity"],
    ["Residential State", "residentialState"],
    ["Residential PIN", "residentialPin"],
    ["Residential Phone", "residentialPhone"],
    ["Residential Mobile", "residentialMobile"],
    ["Residential Email", "residentialEmail"],
    ["Permanent Address", "permanentAddress"],
    ["Permanent Village / City", "permanentCity"],
    ["Permanent State", "permanentState"],
    ["Permanent PIN", "permanentPin"],
    ["Permanent Phone", "permanentPhone"],
    ["Permanent Mobile", "permanentMobile"],
    ["Permanent Email", "permanentEmail"],

    // 04 · Insurance / Sponsor Information
    ["Sponsor Name", "sponsorName"],
    ["Referral Letter Date", "referralLetterDate"],
    ["Referral Letter Number", "referralLetterNumber"],
    ["Group Number", "groupNumber"],
    ["Policy Number", "policyNumber"],
    ["Employee Code", "employeeCode"],
    ["Employee Name", "employeeName"],
    ["Employee Relation", "employeeRelation"],
    ["Basic Salary", "basicSalary"],
    ["Referral Start Date", "referralStartDate"],
    ["Referral End Date", "referralEndDate"],

    // 05 · Registration & Office Use
    ["Patient / Guardian Signature", "patientSignature"],
    ["Signature Date", "signatureDate"],
    ["Registration Date", "registrationDate"],
    ["Patient Registered By", "registeredBy"],

    // System / saved record details
    ["Mobile Number", "mobile"],
    ["Email", "email"],
    ["Visit Count", "visit_count"],
    ["Registration Source", "registration_source"],
  ];

  return (
    <div className="space-y-6 text-slate-800">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#204080]">
            Patient Management
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Patient Registrations
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            View and manage registered patient records.
          </p>
        </div>
        <div className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          <Users size={16} className="text-[#204080]" />
          <span>{patients.length} patients</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                  <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">{stat.description}</p>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-[#204080]">
                  <Icon size={21} strokeWidth={1.8} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Registration Records
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {filteredPatients.length} record(s) found
            </p>
          </div>

          <div className="relative w-full sm:max-w-sm">
            <Search
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              id="registration-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, VPJC ID, mobile or email..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#204080] focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        {error && (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-14 text-sm text-slate-500">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#204080]" />
            Loading registrations...
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <UserRound size={22} />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-800">
              {search ? "No matching registrations found" : "No registrations yet"}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {search
                ? "Try another name, VPJC ID, mobile number or email."
                : "Registered patient records will appear here."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead className="bg-slate-50">
                <tr>
                  {[
                    "S.No",
                    "Patient Name",
                    "VPJC ID",
                    "Mobile",
                    "Age",
                    "Registered On",
                    "Action",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap border-b border-slate-200 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPatients.map((patient, index) => (
                  <tr key={patient.id} className="transition-colors hover:bg-slate-50">
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-500">
                      {index + 1}
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-slate-800">
                      {patient.name || "—"}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm">
                      {patient.vpjc_id ? (
                        <span className="inline-flex rounded-md bg-blue-50 px-2.5 py-1 font-medium text-[#204080]">
                          {patient.vpjc_id}
                        </span>
                      ) : (
                        <span className="text-slate-400">Not assigned</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                      {patient.mobile || patient.residentialMobile || "—"}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      {patient.age ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                      {formatDate(patient.created_at)}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => setSelectedPatient(patient)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-[#204080] transition hover:border-[#204080] hover:bg-blue-50"
                      >
                        <Eye size={14} />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedPatient && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-3 py-5 backdrop-blur-[2px] sm:px-6 sm:py-8"
          onClick={() => setSelectedPatient(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-details-title"
            className="my-0 max-h-[calc(100vh-2.5rem)] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl sm:max-h-[calc(100vh-4rem)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#204080]">
                  Patient Record
                </p>
                <h3
                  id="registration-details-title"
                  className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl"
                >
                  {selectedPatient.name || "Patient Details"}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Registered on {formatDate(selectedPatient.created_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPatient(null)}
                aria-label="Close details"
                className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 sm:p-6">
              {detailFields.map(([label, field]) => {
                const value = selectedPatient[field];

                return (
                  <div
                    key={field}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-3"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {label}
                    </p>
                    <p className="mt-1 break-words text-sm font-medium text-slate-800">
                      {value === undefined || value === null || value === ""
                        ? "—"
                        : String(value)}
                    </p>
                  </div>
                );
              })}
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Registration Date
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">
                  {formatDate(selectedPatient.created_at)}
                </p>
              </div>
            </div>

            <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3 sm:px-6">
              <button
                type="button"
                onClick={() => setSelectedPatient(null)}
                className="rounded-lg bg-[#204080] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#183365]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
